import { HardHat, Landmark, Factory, ShieldCheck, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

const industries = [
  {
    icon: HardHat,
    title: "Construction",
    slug: "construction-it-support",
    desc: "Job site connectivity, BIM security, Procore and Sage integration, and rapid-response IT for teams that can’t afford downtime on pour day.",
    highlights: ["Job Site Networking", "BIM & Cloud Security", "Procore / Sage Support"],
  },
  {
    icon: Landmark,
    title: "Financial Services",
    slug: "financial-it-support",
    desc: "SOC 2, PCI-DSS, and SEC/FINRA-aligned cybersecurity for banks, RIAs, and fintech firms where a single breach means regulatory action.",
    highlights: ["SOC 2 & PCI-DSS", "SEC / FINRA Compliance", "Encrypted Data at Rest & Transit"],
  },
  {
    icon: Factory,
    title: "Manufacturing",
    slug: "manufacturing-it-support",
    desc: "OT/IT convergence security, SCADA protection, and network segmentation for plants where connected machinery meets legacy systems.",
    highlights: ["OT / ICS Security", "SCADA Protection", "Network Segmentation"],
  },
  {
    icon: ShieldCheck,
    title: "High-Compliance Industries",
    slug: "high-compliance-it-support",
    desc: "CMMC, HIPAA, ITAR, and NIST 800-171 expertise for defense contractors, healthcare orgs, and government suppliers with zero tolerance for gaps.",
    highlights: ["CMMC / NIST 800-171", "HIPAA & ITAR", "Audit-Ready Documentation"],
  },
];

export default function Industries() {
  return (
    <section
      id="industries"
      data-testid="industries-section"
      aria-label="Industries served: construction, financial services, manufacturing, and high-compliance"
      className="py-12 lg:py-18 bg-transparent dark-cards relative overflow-hidden"
    >
      <img src="/images/logo-circle.webp" alt="" aria-hidden="true" loading="lazy" className="absolute -left-20 -bottom-20 w-[550px] h-[550px] object-contain opacity-[0.035] brightness-200 pointer-events-none" />
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-10">
          <div className="relative flex items-center justify-center mb-10"><div className="absolute inset-0 flex items-center"><div className="w-full border-t border-white/15"></div></div><span className="relative bg-[#0f1d32]/80 backdrop-blur-sm px-8 py-1.5 rounded text-[#5cc0e8] text-xl font-extrabold uppercase tracking-[0.15em]">Industries We Serve</span></div>
          <h2
            data-testid="industries-heading"
            className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-white mb-4 animate-fade-in-up stagger-1"
            style={{ fontFamily: "Outfit, sans-serif" }}
          >
            Managed IT &amp; cybersecurity built for your industry.
          </h2>
          <p className="text-white/80 text-base max-w-2xl mx-auto animate-fade-in-up stagger-2">
            We specialize in regulated, operational industries where downtime costs more than dollars and compliance isn’t optional.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {industries.map((ind, i) => (
            <div
              key={ind.title}
              data-testid={`industry-card-${i}`}
              className={`grid-border-card p-8 group animate-fade-in-up stagger-${i + 3} !bg-white/[0.08] !border-white/[0.15]`}
            >
              <div className="flex items-start gap-4 mb-5">
                <div className="w-12 h-12 flex-shrink-0 flex items-center justify-center border border-white/15 bg-white/[0.06] group-hover:border-[#0077B3] transition-colors">
                  <ind.icon className="w-6 h-6 text-[#3ab8e8]" />
                </div>
                <div>
                  <h3
                    className="text-white font-semibold text-lg"
                    style={{ fontFamily: "Outfit, sans-serif" }}
                  >
                    {ind.title}
                  </h3>
                </div>
              </div>
              <p className="text-white/90 text-sm leading-relaxed mb-5">{ind.desc}</p>
              <div className="flex flex-wrap gap-2 mb-4">
                {ind.highlights.map((h) => (
                  <span
                    key={h}
                    className="text-xs font-semibold text-white bg-white/10 border border-white/20 px-3 py-1.5 rounded"
                  >
                    {h}
                  </span>
                ))}
              </div>
              {ind.slug && (
                <Link
                  to={`/industries/${ind.slug}`}
                  data-testid={`industry-link-${ind.slug}`}
                  className="inline-flex items-center gap-1 text-[#5cc0e8] text-sm font-bold hover:text-white transition-colors"
                >
                  Learn more <ArrowRight className="w-3 h-3" />
                </Link>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
